/*
 * Yup validators to optional and required dates.
 */
import moment from 'moment'
import * as yup from 'yup'

export const approxDateMessage =
  'If you do not know the exact date, please select an approximate date'

const dayMaxValue = 31
const monthMaxValue = 12
const minYear = 1900
const dateMessage = 'Please enter a valid date.'
const dayMessage = 'Please enter a day value of ' + dayMaxValue + ' or less.'
const dayBlankMessage = 'Day cannot be left blank.'
const minYearMessage =
  'Please enter a year greater than or equal to ' + minYear + '.'
const monthMessage =
  'Please enter a month value of ' + monthMaxValue + ' or less.'
const monthBlankMessage = 'Month cannot be left blank.'
const pastDateMessage = 'Please enter a date in the past.'
const yearMessage = 'Please enter the current year or a year in the past.'
const yearBlankMessage = 'Year cannot be left blank.'

/*
 * All 3 values may be left blank.
 * All 3 values are required if any one of them is entered by the user.
 */
export const dateYupOptional = (pastDateOnly = true) =>
  yup.object().shape({
    // Require the month if the day is entered
    monthValue: yup
      .mixed()
      .test('month_optional', monthMessage, function(value) {
        const { dayValue } = this.parent
        return value ? value > 0 && value <= monthMaxValue : !dayValue
      }),
    // Require the day if the year is entered
    dayValue: yup
      .mixed()
      .test('day_optional', dayMessage, function(value) {
        const { yearValue } = this.parent
        return value ? value > 0 && value <= dayMaxValue : !yearValue
      })
      .test('day_complete_date', dateMessage, function(value) {
        const { yearValue, monthValue } = this.parent
        return (yearValue || monthValue) && !value
          ? moment([yearValue, monthValue - 1, value]).isValid()
          : true
      })
      .test('full_complete_date', dateMessage, function(value) {
        const { yearValue, monthValue } = this.parent
        return yearValue && monthValue && value
          ? moment([yearValue, monthValue - 1, value]).isValid()
          : true
      }),
    // Require the year if month and day are entered
    yearValue: yup
      .mixed()
      .test('year_valid', minYearMessage, function(value) {
        return value ? value >= minYear : true
      })
      .test('year_complete_date', dateMessage, function(value) {
        const { dayValue, monthValue } = this.parent
        return dayValue && monthValue && !value
          ? moment([value, monthValue - 1, dayValue]).isValid()
          : true
      })
      .test('past_date_only', pastDateMessage, function(value) {
        const { dayValue, monthValue } = this.parent
        return value && pastDateOnly
          ? moment([value, monthValue - 1, dayValue]).isSameOrBefore()
          : true
      })
  })

/*
 * A complete date is required to be entered by the user.
 */
export const dateYupRequired = (pastDateOnly = true) =>
  yup.object().shape({
    monthValue: yup
      .number(monthMessage)
      .typeError(monthBlankMessage)
      .integer(monthMessage)
      .positive(monthMessage)
      .max(monthMaxValue, monthMessage)
      .required(monthMessage),
    dayValue: yup
      .number(dayMessage)
      .typeError(dayBlankMessage)
      .integer(dayMessage)
      .positive(dayMessage)
      .max(dayMaxValue, dayMessage)
      .required(dayMessage),
    yearValue: yup
      .number(yearMessage)
      .typeError(yearBlankMessage)
      .integer(yearMessage)
      .positive(yearMessage)
      .min(minYear, minYearMessage)
      .required(yearMessage)
      .test('past_date_only', pastDateMessage, function(value) {
        const { dayValue, monthValue } = this.parent
        return value && pastDateOnly
          ? moment([value, monthValue - 1, dayValue]).isSameOrBefore()
          : true
      })
  })
